<template>
  <div class="container mt-4 mb-5">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="m-0">Kundenverwaltung</h3>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'Invoices' }">
        Zurück zur Rechnungsübersicht
      </router-link>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-md-7">
            <label class="form-label" for="customerSearch">Suche</label>
            <input
              id="customerSearch"
              v-model="search"
              type="text"
              class="form-control"
              placeholder="Name, Kundennummer, Stadt ..."
              @keyup.enter="loadCustomers"
            />
          </div>
          <div class="col-md-2">
            <button class="btn btn-outline-secondary w-100" @click="loadCustomers">
              Suchen
            </button>
          </div>
          <div class="col-md-3">
            <button v-if="canWrite" class="btn btn-primary w-100" @click="openCreate">
              Neuer Kunde
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="canImport || canExport" class="card mb-4">
      <div class="card-body">
        <h5 class="card-title">Import / Export</h5>
        <MasterDataImportPanel v-if="canImport" kind="customer" label="Kunden" :allow-csv="true" @imported="loadCustomers" />
        <div v-if="canExport" class="d-flex gap-2"><select id="customerExportFormat" v-model="exportFormat" class="form-select w-auto" aria-label="Exportformat"><option value="json">JSON</option><option value="csv">CSV</option></select><button class="btn btn-outline-success" @click="exportCustomers">Exportieren</button></div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Kundennr.</th>
                <th>Name</th>
                <th>Ort</th>
                <th>Land</th>
                <th v-if="hasActions" class="text-end">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="customer in customers" :key="customer.id">
                <td>{{ customer.customerNumber || "-" }}</td>
                <td>{{ customer.name }}</td>
                <td>{{ formatCity(customer) }}</td>
                <td>{{ customer.countryCode || "-" }}</td>
                <td v-if="hasActions" class="text-end">
                  <button v-if="canWrite" class="btn btn-sm btn-outline-primary me-2" @click="openEdit(customer)">Bearbeiten</button>
                  <button v-if="canCopy" class="btn btn-sm btn-outline-secondary me-2" @click="openCopy(customer)">Duplizieren</button>
                  <button v-if="canDelete" class="btn btn-sm btn-outline-danger" @click="deleteCustomer(customer)">Löschen</button>
                </td>
              </tr>
              <tr v-if="customers.length === 0">
                <td :colspan="hasActions ? 5 : 4" class="text-center text-muted py-4">Keine Kunden gefunden.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showModal && canWrite" class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEdit ? "Kunde bearbeiten" : isCopy ? "Kunde duplizieren" : "Neuer Kunde" }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <p v-if="isCopy" class="alert alert-info">Kundennummer, Global ID, elektronische Adresse und Steuerkennungen wurden geleert. Die Kopie wird erst beim Speichern angelegt.</p>
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label" for="customerNumber">Kundennummer</label>
                <input id="customerNumber" v-model="form.customerNumber" type="text" class="form-control" />
              </div>
              <div class="col-md-8">
                <label class="form-label" for="customerName">Name *</label>
                <input id="customerName" v-model="form.name" type="text" class="form-control" required />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-8">
                <label class="form-label" for="customerStreet">Straße</label>
                <input id="customerStreet" v-model="form.street" type="text" class="form-control" />
              </div>
              <div class="col-md-4">
                <label class="form-label" for="customerPostcode">PLZ</label>
                <input id="customerPostcode" v-model="form.postcode" type="text" class="form-control" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-6">
                <label class="form-label" for="customerCity">Stadt</label>
                <input id="customerCity" v-model="form.city" type="text" class="form-control" />
              </div>
              <div class="col-md-6">
                <SearchableDropdown
                  input-id="customerCountry"
                  label="Land"
                  :model-value="form.countryCode"
                  @update:modelValue="val => { form.countryCode = val; }"
                  :options="masterDataStore.countryCodes"
                />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-6">
                <label class="form-label" for="customerElectronicAddress">E-Mail / Electronic Address</label>
                <input id="customerElectronicAddress" v-model="form.electronicAddress" type="text" class="form-control" />
              </div>
              <div class="col-md-6">
                <label class="form-label" for="customerGlobalId">Global ID</label>
                <input id="customerGlobalId" v-model="form.globalID" type="text" class="form-control" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-6">
                <SearchableDropdown
                  input-id="customerGlobalIdScheme"
                  label="Global ID Scheme"
                  :model-value="form.globalIDSchemeIdentifier"
                  @update:modelValue="val => { form.globalIDSchemeIdentifier = val; }"
                  :options="masterDataStore.globalIdSchemeIdentifiers"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label" for="customerContactName">Kontaktname</label>
                <input id="customerContactName" v-model="form.contactName" type="text" class="form-control" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-6">
                <label class="form-label" for="customerContactOrgUnit">Org Unit</label>
                <input id="customerContactOrgUnit" v-model="form.contactOrgUnit" type="text" class="form-control" />
              </div>
              <div class="col-md-6">
                <label class="form-label" for="customerContactEmail">Kontakt E-Mail</label>
                <input id="customerContactEmail" v-model="form.contactEmailAddress" type="text" class="form-control" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-6">
                <label class="form-label" for="customerContactPhone">Kontakt Telefon</label>
                <input id="customerContactPhone" v-model="form.contactPhoneNo" type="text" class="form-control" />
              </div>
            </div>

            <div class="mt-4">
              <h5 class="mb-2">Steuerregistrierungen</h5>
              <div v-for="(reg, idx) in form.taxRegistrations" :key="`tax-${idx}`" class="row g-2 align-items-end mb-2">
                <div class="col-md-7">
                  <label class="form-label" :for="`taxRegNo-${idx}`">TaxRegNo</label>
                  <input :id="`taxRegNo-${idx}`" v-model="reg.taxRegNo" type="text" class="form-control" />
                </div>
                <div class="col-md-4">
                  <SearchableDropdown
                    :input-id="`taxScheme-${idx}`"
                    label="TaxSchemeID"
                    :model-value="reg.taxSchemeID"
                    @update:modelValue="val => { reg.taxSchemeID = val; }"
                    :options="masterDataStore.taxRegistrationSchemeIds"
                  />
                </div>
                <div class="col-md-1 text-end">
                  <button class="btn btn-outline-danger" type="button" @click="removeTaxReg(idx)">X</button>
                </div>
              </div>
              <button class="btn btn-outline-secondary btn-sm" type="button" @click="addTaxReg">+ Hinzufügen</button>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" type="button" @click="closeModal">Abbrechen</button>
            <button class="btn btn-primary" type="button" :disabled="!form.name || !canWrite || isSaving" @click="saveCustomer">
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MasterDataImportPanel from "@/components/Administration/MasterDataImportPanel.vue";
import CustomerService from "@/services/CustomerService";
import FileTemplateService from "@/services/FileTemplateService";
import { useUxStore } from "@/stores/uxStore";
import { useMasterDataStore } from "@/stores/masterDataStore";
import { useUserStore } from "@/stores/userStore";
import { copyCustomer } from "@/utils/masterDataCopy.cjs";
import SearchableDropdown from "@/components/Shared/SearchableDropdown.vue";

const emptyForm = () => ({
  id: "",
  customerNumber: "",
  name: "",
  street: "",
  postcode: "",
  city: "",
  countryCode: "DE",
  globalID: "",
  globalIDSchemeIdentifier: "0",
  electronicAddress: "",
  contactName: "",
  contactOrgUnit: "",
  contactEmailAddress: "",
  contactPhoneNo: "",
  taxRegistrations: [],
});

export default {
  name: "CustomersAdminView",
  components: { MasterDataImportPanel, SearchableDropdown },
  data() {
    return {
      customers: [],
      search: "",
      showModal: false,
      isEdit: false,
      isCopy: false,
      isSaving: false,
      form: emptyForm(),
      exportFormat: "json",
      uxStore: useUxStore(),
      masterDataStore: useMasterDataStore(),
      userStore: useUserStore(),
    };
  },
  computed: {
    canWrite() { return this.userStore.hasPermission("customers.write"); },
    canCopy() { return this.canWrite && this.userStore.hasPermission("customers.read"); },
    canDelete() { return this.userStore.hasPermission("customers.delete"); },
    canImport() {
      return this.userStore.hasPermission("customers.import");
    },
    canExport() {
      return this.userStore.hasPermission("customers.export") && this.userStore.hasPermission("files.read");
    },
    hasActions() { return this.canWrite || this.canDelete; },
  },
  methods: {
    formatCity(customer) {
      const parts = [];
      if (customer.postcode) parts.push(customer.postcode);
      if (customer.city) parts.push(customer.city);
      return parts.length ? parts.join(" ") : "-";
    },
    async loadCustomers() {
      const response = await CustomerService.getAll(this.search || null, 1, 200);
      this.customers = Array.isArray(response?.data) ? response.data : [];
    },
    openCreate() {
      if (!this.canWrite) return;
      this.isEdit = false;
      this.isCopy = false;
      this.form = emptyForm();
      this.showModal = true;
    },
    async openEdit(customer) {
      if (!this.canWrite) return;
      this.isEdit = true;
      this.isCopy = false;
      const response = await CustomerService.getById(customer.id);
      const full = response?.data || customer;
      const base = emptyForm();
      this.form = {
        ...base,
        ...full,
        countryCode: full.countryCode || base.countryCode,
        globalIDSchemeIdentifier: full.globalIDSchemeIdentifier || base.globalIDSchemeIdentifier,
        taxRegistrations: Array.isArray(full.taxRegistrations)
          ? full.taxRegistrations.map((x) => ({
              taxRegNo: x.taxRegNo || "",
              taxSchemeID: x.taxSchemeID || "",
            }))
          : [],
      };
      this.showModal = true;
    },
    async openCopy(customer) {
      if (!this.canCopy) return;
      const response = await CustomerService.getById(customer.id);
      if (!response?.data || !this.canCopy) return;
      this.isEdit = false;
      this.isCopy = true;
      this.form = { ...emptyForm(), ...copyCustomer(response.data) };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    addTaxReg() {
      this.form.taxRegistrations.push({ taxRegNo: "", taxSchemeID: "" });
    },
    removeTaxReg(idx) {
      this.form.taxRegistrations.splice(idx, 1);
    },
    async saveCustomer() {
      if (!this.canWrite || this.isSaving) return;
      this.isSaving = true;
      try {
        const payload = {
          customerNumber: this.form.customerNumber || null,
          name: this.form.name,
          street: this.form.street || null,
          postcode: this.form.postcode || null,
          city: this.form.city || null,
          countryCode: this.form.countryCode || null,
          globalID: this.form.globalID || null,
          globalIDSchemeIdentifier: this.form.globalIDSchemeIdentifier || null,
          electronicAddress: this.form.electronicAddress || null,
          contactName: this.form.contactName || null,
          contactOrgUnit: this.form.contactOrgUnit || null,
          contactEmailAddress: this.form.contactEmailAddress || null,
          contactPhoneNo: this.form.contactPhoneNo || null,
          taxRegistrations: (this.form.taxRegistrations || []).filter((x) => x.taxRegNo && x.taxRegNo.trim().length > 0),
        };

        if (this.isEdit) {
          await CustomerService.update(this.form.id, payload);
        } else {
          await CustomerService.create(payload);
        }

        this.closeModal();
        await this.loadCustomers();
      } finally {
        this.isSaving = false;
      }
    },
    async deleteCustomer(customer) {
      if (!this.canDelete) return;
      const confirmed = window.confirm(`Kunde "${customer.name}" wirklich löschen?`);
      if (!confirmed) return;
      await CustomerService.remove(customer.id);
      await this.loadCustomers();
    },
    async exportCustomers() {
      if (!this.canExport) return;
      const response = await CustomerService.export(this.exportFormat);
      const data = response?.data;
      if (!data?.fileId) {
        return;
      }

      await FileTemplateService.downloadFile(data.fileId, data.fileName || "customers");
    },
  },
  mounted() {
    if (!this.userStore.me) this.userStore.loadMe().catch(() => {});
    this.loadCustomers();

    // Keep the admin modules consistent with the invoice module: use masterdata-backed dropdowns.
    this.masterDataStore.fetchCountryCodes();
    this.masterDataStore.fetchGlobalIdSchemeIdentifiers();
    this.masterDataStore.fetchTaxRegistrationSchemeIds();
  },
};
</script>
